"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, PhoneCall, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, Navigation, Wrench, Building2, House as Home } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const neighborhoods = [{
  name: "Historic Downtown Jonesboro",
  zip: "30236",
  eta: "15-25 Mins",
  type: "Residential & Commercial"
}, {
  name: "Tara Boulevard Corridor",
  zip: "30236 / 30238",
  eta: "15-30 Mins",
  type: "Commercial & Strip Centers"
}, {
  name: "Lake Spivey & Surrounding",
  zip: "30236",
  eta: "20-35 Mins",
  type: "Lakefront & High-Value Homes"
}, {
  name: "Irondale & Flint River Road",
  zip: "30238",
  eta: "20-30 Mins",
  type: "Residential Subdivisions"
}, {
  name: "Clayton State University Area",
  zip: "30260 / 30236",
  eta: "20-35 Mins",
  type: "Campus & Multi-Family Units"
}, {
  name: "Lovejoy & South Clayton",
  zip: "30250",
  eta: "25-40 Mins",
  type: "Single-Family Neighborhoods"
}];
const dispatchHighlights = [{
  title: "15 to 45 Minute Average Arrival",
  desc: "Mobile service units stationed across Clayton County for fast emergency routing."
}, {
  title: "Fully Stocked Master Vans",
  desc: "We carry commercial snaking gear, leak detection tools, and replacement valves on board."
}, {
  title: "Zero Hidden Night/Weekend Fees",
  desc: "Clear, upfront quotes before work starts regardless of time or holiday status."
}, {
  title: "Licensed & Insured GA Plumbers",
  desc: "Experienced local technicians adhering strictly to Georgia residential plumbing codes."
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="serviceareajonesboro" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={itemVariants} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 gap-1.5 text-xs font-semibold uppercase tracking-wide">
            <MapPin className="size-3.5 text-accent" />
            Clayton County Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            24/7 Emergency Plumbing Across Jonesboro, GA
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Based right here in Jonesboro, our rapid-response teams are staged to resolve broken water lines, sewer backups, and water heater failures in minutes.
          </p>
        </motion.div>

        {/* Main Grid: Map/Status overview + Key neighborhoods */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Left Column: Live Status & Dispatch Box */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={itemVariants} className="lg:col-span-5 flex flex-col gap-6">
            <Card className="bg-primary text-primary-foreground border-none shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 translate-x-6 -translate-y-6 opacity-10 pointer-events-none">
                <Navigation className="size-48" />
              </div>
              <CardHeader className="relative z-10 pb-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold px-2.5 py-1 rounded-full bg-accent text-accent-foreground">
                    <span className="size-2 rounded-full bg-accent-foreground animate-pulse" />
                    Live Clayton County Dispatch
                  </span>
                  <span className="text-xs text-primary-foreground/80 font-mono">
                    Updated Just Now
                  </span>
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-bold font-default mt-4 text-primary-foreground">
                  Jonesboro Rapid Dispatch Center
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 text-sm">
                  Service trucks stationed along Tara Blvd, GA-54, and GA-138 ready to roll immediately.
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10 flex flex-col gap-5 pt-2">
                <div className="p-4 rounded-lg bg-primary-foreground/10 border border-primary-foreground/15 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="size-6 text-accent shrink-0" />
                    <div>
                      <p className="text-xs text-primary-foreground/70 uppercase tracking-wider font-semibold">Typical Response Time</p>
                      <p className="text-lg font-bold text-primary-foreground">15 - 45 Minutes</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-primary-foreground/70 uppercase tracking-wider font-semibold">Availability</p>
                    <p className="text-lg font-bold text-accent">24/7 / 365</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/75">
                    Immediate Assistance For:
                  </p>
                  <ul className="space-y-2 text-sm text-primary-foreground/90">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Burst pipes and major indoor slab leaks</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Sewage backing up into tubs, sinks, or toilets</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-accent shrink-0" />
                      <span>Water heater flooding and total hot water loss</span>
                    </li>
                  </ul>
                </div>

                <Separator className="bg-primary-foreground/20 my-1" />

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <a href="tel:4708479076" className="flex-1">
                    <Button variant="default" size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-md">
                      <PhoneCall className="size-4" />
                      Call (470) 847-9076
                    </Button>
                  </a>
                  <Button variant="secondary" size="lg" asChild className="bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25 border-none">
                    <Link to="/contact/">
                      Request Online
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Micro guarantee banner */}
            <div className="p-4 rounded-xl bg-muted/70 border border-border flex items-center gap-3.5">
              <ShieldCheck className="size-6 text-primary shrink-0" />
              <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                <strong className="text-foreground font-semibold">Local Jonesboro Guarantee:</strong> Experienced plumbers with complete vehicle GPS dispatch across south Atlanta.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Neighborhoods Breakdown Grid */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} variants={containerVariants} className="lg:col-span-7 flex flex-col gap-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground">
                Neighborhoods & Communities We Prioritize
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Direct route coverage for homes, apartments, retail parks, and commercial facilities in Clayton County.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {neighborhoods.map((item, i) => <div key={item.name} data-index={i} className="p-4 rounded-xl bg-card text-card-foreground border border-border shadow-xs hover:border-primary/40 hover:shadow-md transition-all flex flex-col justify-between gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        {i % 2 === 0 ? <Home className="size-4" /> : <Building2 className="size-4" />}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold font-default text-foreground leading-snug">
                          {item.name}
                        </h4>
                        <span className="text-xs text-muted-foreground font-mono">
                          ZIP {item.zip}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-border/60" />

                  <div className="flex items-center justify-between text-xs pt-1">
                    <span className="text-muted-foreground">{item.type}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                      <Clock className="size-3 text-accent" />
                      {item.eta}
                    </span>
                  </div>
                </div>)}
            </div>

            {/* Additional surrounding areas info banner */}
            <div className="p-4 rounded-xl bg-card border border-border text-card-foreground flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Wrench className="size-5 text-accent shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Need service just outside Jonesboro city limits?</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">We also dispatch to Riverdale, Morrow, Stockbridge, and McDonough.</p>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild className="shrink-0">
                <Link to="/service-areas/">
                  <span>View All Areas</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Bottom Feature Pillars */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-6 border-t border-border">
          {dispatchHighlights.map((feat, i) => <div key={feat.title} data-index={i} className="flex flex-col gap-2 p-4 rounded-lg bg-card/60 border border-border/70">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <h4 className="text-sm font-bold font-default text-foreground">
                  {feat.title}
                </h4>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed pl-6">
                {feat.desc}
              </p>
            </div>)}
        </motion.div>
      </div>
    </section>;
}